import { forwardRef, memo, useState, useCallback, useMemo, type Ref, type MouseEvent } from 'react';
import size from 'lodash/size';
import { useIntl, FormattedMessage } from 'react-intl';
// Material UI imports
import type { TypographyVariant } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import Avatar from '@mui/material/Avatar';
import IconButton, { type IconButtonProps } from '@mui/material/IconButton';
import Tooltip, { type TooltipProps } from '@mui/material/Tooltip';
// import ClickAwayListener from '@mui/material/ClickAwayListener';
// Material Icon imports
import InformationVariant from 'mdi-material-ui/InformationVariant';
// local imports
import { commonFormattingValues, Values } from '../helpers/intl';
import StandardLink from './StandardLink';
// SCSS imports
import { avatar, smaller, enabled, muted, iconButton } from './InfoButton.module.scss';

type InfoButtonProps = {
  readonly help: string;
  readonly values?: Values;
  readonly text?: string;
  readonly textVariant?: TypographyVariant | 'inherit';
  readonly disabled?: boolean | null;
  readonly small?: boolean | null;
  readonly placement?: TooltipProps['placement'];
  readonly enablePortal?: boolean | null;
  readonly tooltipSx?: TooltipProps['sx'];
  readonly buttonSx?: IconButtonProps['sx'];
  // for Storybook only
  readonly isOpen?: boolean;
}

const InfoButton = forwardRef<HTMLButtonElement | HTMLAnchorElement, InfoButtonProps>(({
  help,
  values,
  text,
  textVariant,
  disabled = false,
  small = false,
  placement = 'top',
  enablePortal = false,
  isOpen = false,
  tooltipSx,
  buttonSx
}, ref) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { formatMessage } = useIntl();

  const [open, setOpen] = useState(isOpen);

  const toggleTooltip = useCallback((
    event?: MouseEvent<HTMLButtonElement> | MouseEvent<HTMLAnchorElement> | MouseEvent<HTMLSpanElement>
  ) => {
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }
    setOpen((prevOpen) => !prevOpen);
  }, []);

  const handleTooltipClose = useCallback(() => {
    setOpen(false);
  }, []);

  const handleTooltipOpen = useCallback(() => {
    setOpen(true);
  }, []);

  const vals = useMemo(() => values && size(values) >= 1
    ? { ...commonFormattingValues, ...values } : commonFormattingValues, [values]);

  return (
    // <ClickAwayListener onClickAway={handleTooltipClose}>
    <Tooltip
        open={open}
        onOpen={handleTooltipOpen}
        onClose={handleTooltipClose}
        disableInteractive
        arrow
        // disableFocusListener
        disableHoverListener
        placement={isMobile ? 'auto' : placement}
        title={<FormattedMessage id={help} defaultMessage={help} values={vals}/>}
        slotProps={enablePortal ? undefined : { popper: { disablePortal: true } }}
        sx={tooltipSx}
    >
      {text ? (
        <StandardLink
            ref={ref as Ref<HTMLAnchorElement>}
            sx={buttonSx}
            variant={small ? 'body2' : textVariant}
            text={text}
            onClick={disabled ? undefined : toggleTooltip}
        />
      ) : (
        <IconButton
            ref={ref as Ref<HTMLButtonElement>}
            sx={buttonSx}
            size={small ? 'small' : 'medium'}
            disabled={disabled ? true : undefined}
            color="primary"
            aria-label={formatMessage({ id: 'common.button.info' })}
            onClick={toggleTooltip}
            className={iconButton}
        >
          <Avatar
              variant="circular"
              className={`${disabled ? muted : enabled} ${small ? smaller : avatar}`}
          >
            <InformationVariant fontSize="inherit"/>
          </Avatar>
        </IconButton>
      )}
    </Tooltip>
    // </ClickAwayListener>
  );
});

InfoButton.displayName = 'InfoButton';

export default memo(InfoButton);
